import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, ChevronDown, ChevronUp, X } from 'lucide-react';
import {
  OCCASIONS,
  READY_IN_OPTIONS,
  TAG_GROUPS,
  countFilters,
  swatchFor,
  type PostFilters,
  type ReadyIn,
  type TagGroup,
} from '@/config/filters';
import { filterTagsService } from '@/services/filterTags.service';

interface PostFiltersSectionProps {
  value: PostFilters;
  onChange: (next: PostFilters) => void;
  disabled?: boolean;
}

const chipBase =
  'inline-flex items-center justify-center min-h-[40px] px-4 rounded-full border text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
const chipIdle = 'border-gray-200 bg-white text-gray-700 hover:border-gray-400';
const chipActive = 'border-gray-900 bg-gray-900 text-white';

/** Highlight the typed fragment inside a suggestion, as the picker does. */
const Highlighted: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  const at = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark className="bg-amber-100 text-gray-900 rounded-sm">{text.slice(at, at + query.length)}</mark>
      {text.slice(at + query.length)}
    </>
  );
};

/**
 * "Help clients find it" — the occasion, tag and ready-time answers that put a
 * post behind the filters clients actually browse with on the inner pages.
 * Collapsed by default so the create flow still reads as photo → caption → post.
 */
const PostFiltersSection: React.FC<PostFiltersSectionProps> = ({ value, onChange, disabled }) => {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherValue, setOtherValue] = useState('');
  // The live vocabulary, with the bundled list as the starting point so the
  // picker is usable before the request lands.
  const [vocabulary, setVocabulary] = useState<TagGroup[]>(TAG_GROUPS);
  const tagFieldRef = useRef<HTMLDivElement>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const count = countFilters(value);

  useEffect(() => {
    let cancelled = false;
    filterTagsService.list().then((groups) => {
      if (!cancelled) setVocabulary(groups);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // The panel opens in flow, so scroll it into view rather than letting it
  // push itself below the fold of whichever modal is hosting the section.
  useEffect(() => {
    if (!showSuggestions) return;
    const id = window.setTimeout(
      () => panelRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }),
      60,
    );
    return () => window.clearTimeout(id);
  }, [showSuggestions]);

  // Dismiss the suggestion list on an outside click, like any other combobox.
  useEffect(() => {
    if (!showSuggestions) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!tagFieldRef.current?.contains(e.target as Node)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [showSuggestions]);

  const toggleOccasion = (occasion: string) =>
    onChange({
      ...value,
      occasions: value.occasions.includes(occasion)
        ? value.occasions.filter((o) => o !== occasion)
        : [...value.occasions, occasion],
    });

  const addTag = (label: string, isNew = false) => {
    const tag = label.trim().toLowerCase();
    if (!tag || value.tags.includes(tag)) {
      setQuery('');
      return;
    }
    // A term that isn't in the vocabulary goes to the team for review. It is
    // still applied to the post — review only governs the shared picker.
    if (isNew) filterTagsService.submit(label.trim());
    onChange({ ...value, tags: [...value.tags, tag] });
    setQuery('');
  };

  const removeTag = (tag: string) => onChange({ ...value, tags: value.tags.filter((t) => t !== tag) });

  const setReadyIn = (option: ReadyIn) =>
    onChange({ ...value, readyIn: value.readyIn === option ? null : option });

  const addOther = () => {
    const next = otherValue.trim();
    if (next && !value.occasions.some((o) => o.toLowerCase() === next.toLowerCase())) {
      onChange({ ...value, occasions: [...value.occasions, next] });
    }
    setOtherValue('');
    setOtherOpen(false);
  };

  // Suggestions mirror the inner-page facets, filtered by what's been typed.
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return vocabulary.map((group) => ({
      name: group.name,
      tags: group.tags.filter(
        (t) => !value.tags.includes(t.label.toLowerCase()) && (!q || t.label.toLowerCase().includes(q)),
      ),
    })).filter((group) => group.tags.length > 0);
  }, [query, value.tags, vocabulary]);

  const matchCount = groups.reduce((sum, g) => sum + g.tags.length, 0);
  // A term already in the vocabulary is picked, not submitted.
  const curatedLabels = useMemo(
    () => new Set(vocabulary.flatMap((g) => g.tags.map((t) => t.label.toLowerCase()))),
    [vocabulary],
  );
  const canAddNew = query.trim().length > 0 && !curatedLabels.has(query.trim().toLowerCase());

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(query, canAddNew);
    } else if (e.key === 'Backspace' && query === '' && value.tags.length > 0) {
      removeTag(value.tags[value.tags.length - 1]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const summaryChips = [
    ...value.occasions,
    ...value.tags.map((t) => t.charAt(0).toUpperCase() + t.slice(1)),
    ...(value.readyIn ? [`Ready in ${value.readyIn}`] : []),
  ];

  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      {/* Header — tap anywhere to open */}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="flex-shrink-0 w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
          <Search size={16} className="text-amber-600" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-gray-900">Help clients find it</span>
          <span className="block text-xs text-gray-500 mt-0.5">
            {expanded ? 'Occasion, tags and ready time' : 'Clients will find this under these filters'}
          </span>
        </span>
        {count > 0 && (
          <span className="flex-shrink-0 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
            {count} added
          </span>
        )}
        {expanded ? (
          <ChevronUp size={16} className="flex-shrink-0 text-gray-400" />
        ) : (
          <ChevronDown size={16} className="flex-shrink-0 text-gray-400" />
        )}
      </button>

      {/* Collapsed summary */}
      {!expanded && summaryChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-4 pb-4">
          {summaryChips.map((chip) => (
            <span
              key={chip}
              className="inline-flex items-center px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium"
            >
              {chip}
            </span>
          ))}
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-xs font-semibold text-gray-900 underline underline-offset-2 hover:text-gray-600 transition-colors px-1 py-1.5"
          >
            Edit
          </button>
        </div>
      )}

      {expanded && (
        <div className="px-4 pb-4 space-y-5 animate-in fade-in slide-in-from-top-1 duration-200">
          <p className="text-xs text-gray-500 leading-relaxed">
            These match the filters on our inner pages, so your dress shows up in the right edits.
          </p>

          {/* Occasion */}
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-3">
              Occasion <span className="font-normal text-gray-400">· pick all that fit, or add your own</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {OCCASIONS.map((occasion) => (
                <button
                  key={occasion}
                  type="button"
                  disabled={disabled}
                  onClick={() => toggleOccasion(occasion)}
                  aria-pressed={value.occasions.includes(occasion)}
                  className={`${chipBase} ${value.occasions.includes(occasion) ? chipActive : chipIdle}`}
                >
                  {occasion}
                </button>
              ))}
              {/* Custom occasions the creator added */}
              {value.occasions
                .filter((o) => !OCCASIONS.includes(o as (typeof OCCASIONS)[number]))
                .map((occasion) => (
                  <button
                    key={occasion}
                    type="button"
                    disabled={disabled}
                    onClick={() => toggleOccasion(occasion)}
                    className={`${chipBase} ${chipActive} gap-1.5`}
                  >
                    {occasion}
                    <X size={13} />
                  </button>
                ))}
              {otherOpen ? (
                <input
                  autoFocus
                  value={otherValue}
                  onChange={(e) => setOtherValue(e.target.value)}
                  onBlur={addOther}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addOther();
                    } else if (e.key === 'Escape') {
                      setOtherValue('');
                      setOtherOpen(false);
                    }
                  }}
                  placeholder="Your occasion"
                  className="min-h-[40px] px-4 rounded-full border border-gray-900 text-sm w-36 focus:outline-none"
                />
              ) : (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => setOtherOpen(true)}
                  className={`${chipBase} border-dashed border-gray-300 bg-white text-gray-500 hover:border-gray-400 hover:text-gray-700`}
                >
                  + Other
                </button>
              )}
            </div>
          </div>

          {/* Tags */}
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-3">
              Tags <span className="font-normal text-gray-400">(colour, fabric, length, style)</span>
            </p>
            <div ref={tagFieldRef}>
              <div
                onClick={() => {
                  if (!disabled) {
                    tagInputRef.current?.focus();
                    setShowSuggestions(true);
                  }
                }}
                className={`flex flex-wrap items-center gap-2 w-full min-h-[52px] px-3 py-2 rounded-2xl border transition-all duration-200 cursor-text ${
                  showSuggestions ? 'border-gray-900 ring-2 ring-gray-900/10' : 'border-gray-200'
                } ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
              >
                <Search size={15} className="flex-shrink-0 text-gray-400" />
                {value.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 bg-gray-100 text-gray-800 text-sm rounded-full font-medium"
                  >
                    {swatchFor(tag) && (
                      <span
                        className="w-2.5 h-2.5 rounded-full border border-black/10"
                        style={{ backgroundColor: swatchFor(tag) }}
                      />
                    )}
                    {tag.charAt(0).toUpperCase() + tag.slice(1)}
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTag(tag);
                      }}
                      aria-label={`Remove ${tag}`}
                      className="text-gray-400 hover:text-gray-700 transition-colors"
                    >
                      <X size={13} />
                    </button>
                  </span>
                ))}
                <input
                  ref={tagInputRef}
                  type="text"
                  value={query}
                  disabled={disabled}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onKeyDown={handleTagKeyDown}
                  placeholder={value.tags.length === 0 ? 'Search filters: red, lace, mini, …' : ''}
                  className="flex-1 min-w-[120px] bg-transparent text-sm py-1.5 placeholder:text-gray-400 focus:outline-none disabled:cursor-not-allowed"
                />
                {(query || value.tags.length > 0) && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setQuery('');
                      onChange({ ...value, tags: [] });
                    }}
                    aria-label="Clear tags"
                    className="flex-shrink-0 text-gray-400 hover:text-gray-700 transition-colors"
                  >
                    <X size={15} />
                  </button>
                )}
              </div>

              {/* Suggestions — the same vocabulary clients filter with */}
              {showSuggestions && (
                <div
                  ref={panelRef}
                  className="mt-2 rounded-2xl border border-gray-200 bg-white shadow-lg overflow-hidden"
                >
                  <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-gray-100">
                    <p className="text-xs text-gray-500">
                      {query.trim()
                        ? `${matchCount} result${matchCount === 1 ? '' : 's'} for “${query.trim()}”`
                        : 'Pick a filter'}
                    </p>
                    <p className="text-xs text-gray-400 flex-shrink-0">Same filters clients use</p>
                  </div>

                  <div className="max-h-[300px] overflow-y-auto overscroll-contain p-3 space-y-3">
                    {canAddNew && (
                      <button
                        type="button"
                        onClick={() => addTag(query, true)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border border-gray-200 hover:border-gray-400 hover:bg-gray-50 transition-colors text-left"
                      >
                        <span className="text-sm text-gray-800">
                          + Add “{query.trim()}” as a new tag
                        </span>
                        <span className="text-xs font-semibold text-red-500 flex-shrink-0">
                          Reviewed by our team
                        </span>
                      </button>
                    )}

                    {groups.map((group) => (
                      <div key={group.name}>
                        <div className="flex items-center justify-between gap-3 px-1 mb-2">
                          <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-gray-400">
                            {group.name}
                          </p>
                          <p className="text-[10px] text-gray-400 flex-shrink-0">
                            {group.tags.length} match{group.tags.length === 1 ? '' : 'es'}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {group.tags.map((tag) => (
                            <button
                              key={tag.label}
                              type="button"
                              onClick={() => addTag(tag.label)}
                              className="inline-flex items-center gap-2 min-h-[38px] px-3.5 rounded-full border border-gray-200 bg-white text-sm text-gray-800 hover:border-gray-900 hover:bg-gray-50 transition-colors"
                            >
                              {tag.swatch && (
                                <span
                                  className="w-2.5 h-2.5 rounded-full border border-black/10"
                                  style={{ backgroundColor: tag.swatch }}
                                />
                              )}
                              <Highlighted text={tag.label} query={query.trim()} />
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}

                    {groups.length === 0 && !canAddNew && (
                      <p className="px-1 py-4 text-sm text-gray-400 text-center">
                        Every matching filter is already added.
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-gray-100 bg-gray-50/60">
                    <p className="text-[11px] text-gray-500">
                      Enter or comma adds · Backspace removes last
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowSuggestions(false)}
                      className="flex-shrink-0 px-4 py-1.5 rounded-full bg-gray-900 text-white text-xs font-semibold hover:bg-gray-800 transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2 leading-relaxed">
              Pick from the list so your post shows up when clients filter. Anything new gets a quick
              check by our team.
            </p>
          </div>

          {/* Ready in */}
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-3">
              Ready in <span className="font-normal text-gray-400">(optional)</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {READY_IN_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  disabled={disabled}
                  onClick={() => setReadyIn(option)}
                  aria-pressed={value.readyIn === option}
                  className={`${chipBase} ${value.readyIn === option ? chipActive : chipIdle}`}
                >
                  {option}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">How long you usually need to make this piece.</p>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                setShowSuggestions(false);
                setExpanded(false);
              }}
              className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Done · collapse
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostFiltersSection;
